import type { SpecialtyType } from '@/types/database'

// ─── Platform business rules ─────────────────────────────────────────────────

/**
 * Taxa da plataforma, em % — ACRÉSCIMO sobre o preço do instrutor.
 *
 * O instrutor define o valor da hora dele (`hourly_rate`) e recebe esse valor
 * inteiro. O aluno paga `hourly_rate × (1 + PLATFORM_FEE_PERCENT/100)`:
 * instrutor cobra R$ 60 → aluno paga R$ 84 → plataforma fica com R$ 24.
 * Use `getStudentPrice` (lib/utils) para exibir preço ao aluno.
 *
 * Fonte da verdade em runtime é `platform_settings.platform_fee_percent`
 * (lido pela edge function `create-checkout`); esta constante é o espelho
 * usado no preview do front e deve ser mantida em sincronia.
 */
export const PLATFORM_FEE_PERCENT = 40

/**
 * Desconto do aluno Premium em todas as aulas, em %. Espelho de
 * `platform_settings.premium_discount_percent` (lido pelo create-checkout).
 * Sai da parte da plataforma — o repasse do instrutor não muda.
 */
export const PREMIUM_DISCOUNT_PERCENT = 10

/** Minutes after booking creation during which the slot is held for payment. */
export const BOOKING_LOCK_MINUTES = 15

/**
 * Faixas de reembolso por antecedência do cancelamento.
 *
 * Espelham a tabela `cancellation_policies`, que é a fonte da verdade lida pela
 * RPC `calculate_refund_amount`. Qualquer texto de UI que fale de reembolso
 * deve derivar daqui — antes desta constante existir, a landing, a central de
 * ajuda e o componente `CancellationPolicy` anunciavam três tabelas diferentes.
 */
export const CANCELLATION_TIERS: { hoursBefore: number; refundPercent: number }[] = [
  { hoursBefore: 2, refundPercent: 100 },
  { hoursBefore: 0, refundPercent: 0 },
]

/** Horas antes da aula até quando o cancelamento devolve o valor ao saldo. */
export const CANCELLATION_FULL_REFUND_HOURS = CANCELLATION_TIERS[0].hoursBefore

/**
 * Números de vitrine usados no marketing (landing, "seja instrutor", login).
 *
 * ⚠️ São valores de fachada, não medições — ficam centralizados aqui porque as
 * três telas exibiam contagens diferentes entre si (500+, 800 e 12k+ de
 * instrutores na mesma sessão de navegação).
 */
export const PLATFORM_STATS = {
  instructors: 500,
  students: 12000,
  lessonsDelivered: 10000,
  averageRating: 4.9,
  cities: 50,
} as const

/** Hours after lesson completion before instructor payout is released. */
export const PAYOUT_WINDOW_HOURS = 48

// ─── Specialty labels ────────────────────────────────────────────────────────

export const SPECIALTY_LABELS: Record<SpecialtyType, string> = {
  iniciantes: 'Para Iniciantes',
  cnh_b: 'CNH Categoria B',
  cnh_a: 'CNH Categoria A',
  direcao_defensiva: 'Direção Defensiva',
  cambio_manual: 'Câmbio Manual',
  cambio_automatico: 'Câmbio Automático',
  estacionamento: 'Estacionamento',
  rodovia: 'Rodovias e Estradas',
}

// ─── Calendar helpers ─────────────────────────────────────────────────────────

/** Portuguese day names indexed 0 (Sunday) → 6 (Saturday). */
export const DAY_OF_WEEK_LABELS: string[] = [
  'Domingo',
  'Segunda-feira',
  'Terça-feira',
  'Quarta-feira',
  'Quinta-feira',
  'Sexta-feira',
  'Sábado',
]

// ─── Lesson duration options ──────────────────────────────────────────────────

export const DURATION_OPTIONS: { value: number; label: string }[] = [
  { value: 60, label: '1 hora' },
  { value: 90, label: '1 hora e 30 minutos' },
  { value: 120, label: '2 horas' },
]

/** Duration for a trial lesson in minutes. */
export const TRIAL_DURATION_MINUTES = 30

/** Price multiplier for a trial lesson (50% of the normal rate). */
export const TRIAL_PRICE_MULTIPLIER = 0.5

// ─── Brazilian states ─────────────────────────────────────────────────────────

export const BRAZILIAN_STATES: { value: string; label: string }[] = [
  { value: 'AC', label: 'AC — Acre' },
  { value: 'AL', label: 'AL — Alagoas' },
  { value: 'AP', label: 'AP — Amapá' },
  { value: 'AM', label: 'AM — Amazonas' },
  { value: 'BA', label: 'BA — Bahia' },
  { value: 'CE', label: 'CE — Ceará' },
  { value: 'DF', label: 'DF — Distrito Federal' },
  { value: 'ES', label: 'ES — Espírito Santo' },
  { value: 'GO', label: 'GO — Goiás' },
  { value: 'MA', label: 'MA — Maranhão' },
  { value: 'MT', label: 'MT — Mato Grosso' },
  { value: 'MS', label: 'MS — Mato Grosso do Sul' },
  { value: 'MG', label: 'MG — Minas Gerais' },
  { value: 'PA', label: 'PA — Pará' },
  { value: 'PB', label: 'PB — Paraíba' },
  { value: 'PR', label: 'PR — Paraná' },
  { value: 'PE', label: 'PE — Pernambuco' },
  { value: 'PI', label: 'PI — Piauí' },
  { value: 'RJ', label: 'RJ — Rio de Janeiro' },
  { value: 'RN', label: 'RN — Rio Grande do Norte' },
  { value: 'RS', label: 'RS — Rio Grande do Sul' },
  { value: 'RO', label: 'RO — Rondônia' },
  { value: 'RR', label: 'RR — Roraima' },
  { value: 'SC', label: 'SC — Santa Catarina' },
  { value: 'SP', label: 'SP — São Paulo' },
  { value: 'SE', label: 'SE — Sergipe' },
  { value: 'TO', label: 'TO — Tocantins' },
]
